import type { CSSProperties } from "react";

/**
 * Hero background-overlay styles. `undefined` (= "flat") keeps the current
 * behavior byte-identical: a solid `backgroundColor` fill from
 * `backgroundOverlayColor`. The non-flat styles build a gradient out of the
 * SAME `backgroundOverlayColor` (its rgba/hex is parsed into stops), so the
 * user's existing color choice keeps driving the overlay.
 *
 * Runtime twin of main-frontend
 * `src/components/WebsiteBuilder/sections/utils/overlayStyle.ts` — keep the
 * two files behavior-identical.
 */
export type HeroOverlayStyle =
  | "scrim-bottom"
  | "scrim-top"
  | "scrim-left"
  | "vignette";

type Rgba = { r: number; g: number; b: number; a: number };

const clamp01 = (n: number) => Math.min(1, Math.max(0, n));

/** Parse #rgb/#rgba/#rrggbb/#rrggbbaa or rgb()/rgba() into channels. */
export const parseOverlayColor = (color: string): Rgba | null => {
  const value = color.trim();

  const hexMatch = value.match(/^#([0-9a-fA-F]{3,8})$/);
  if (hexMatch) {
    const hex = hexMatch[1] ?? "";
    if (hex.length === 3 || hex.length === 4) {
      // defaults never fire (length-checked); they only narrow the types
      const [r = 0, g = 0, b = 0, a = 255] = hex
        .split("")
        .map((c) => parseInt(c + c, 16));
      return { r, g, b, a: hex.length === 4 ? clamp01(a / 255) : 1 };
    }
    if (hex.length === 6 || hex.length === 8) {
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      const a = hex.length === 8 ? clamp01(parseInt(hex.slice(6, 8), 16) / 255) : 1;
      return { r, g, b, a };
    }
    return null;
  }

  const rgbMatch = value.match(
    /^rgba?\(\s*(\d{1,3})\s*[, ]\s*(\d{1,3})\s*[, ]\s*(\d{1,3})\s*(?:[,/]\s*([\d.]+%?)\s*)?\)$/,
  );
  if (rgbMatch) {
    // group defaults never fire (1-3 are non-optional); they narrow the types
    const [, r = "0", g = "0", b = "0", aRaw] = rgbMatch;
    let a = 1;
    if (aRaw !== undefined) {
      a = aRaw.endsWith("%") ? parseFloat(aRaw) / 100 : parseFloat(aRaw);
      if (Number.isNaN(a)) return null;
    }
    return {
      r: Math.min(255, parseInt(r, 10)),
      g: Math.min(255, parseInt(g, 10)),
      b: Math.min(255, parseInt(b, 10)),
      a: clamp01(a),
    };
  }

  return null;
};

/**
 * Style object for the hero overlay div.
 * flat/undefined (or an unparseable color) → `{ backgroundColor }` exactly as
 * before; otherwise a gradient built from the parsed color.
 */
export const overlayBackgroundStyle = (
  backgroundOverlayColor: string | undefined,
  overlayStyle?: HeroOverlayStyle,
): CSSProperties => {
  const flat: CSSProperties = { backgroundColor: backgroundOverlayColor };
  if (!overlayStyle || !backgroundOverlayColor) return flat;

  const parsed = parseOverlayColor(backgroundOverlayColor);
  if (!parsed) return flat;

  const stop = (alphaScale: number) =>
    `rgba(${parsed.r}, ${parsed.g}, ${parsed.b}, ${+(parsed.a * alphaScale).toFixed(3)})`;

  switch (overlayStyle) {
    case "scrim-bottom":
      return { background: `linear-gradient(to top, ${stop(1)} 0%, ${stop(0)} 75%)` };
    case "scrim-top":
      return { background: `linear-gradient(to bottom, ${stop(1)} 0%, ${stop(0)} 75%)` };
    case "scrim-left":
      return { background: `linear-gradient(to right, ${stop(1)} 0%, ${stop(0)} 75%)` };
    case "vignette":
      return {
        background: `radial-gradient(ellipse at center, ${stop(0)} 40%, ${stop(1)} 100%)`,
      };
    default:
      return flat;
  }
};
